import React, { useState, useEffect, useMemo } from 'react';
import Highcharts from 'highcharts';
import ResponsiveHighchartsReact from '../common/ResponsiveHighchartsReact';
import DemandFuelPriceDocumentationAccordion from '../tabs/countryDashboard/subComponents/demandFuelPrice/DemandFuelPriceDocumentationAccordion';
import { energyMetricsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { smartFormatNumber } from '../../utils/numberFormatting';
import {  createLineChartConfig,  createStackedColumnChartConfig} from '../../utils/highchartsConfig';
import {  getFuelColor,  initializeFuelColors} from '../../utils/fuelColors';

const ChartCard = ({ options }) => {
  if (!options) return null;

  return (
    <div className="bg-white rounded-lg shadow-sm w-full min-w-0 overflow-hidden sm:p-0 md:p-2 lg:p-4">
      <div className="w-full min-w-0 overflow-x-auto px-0 lg:px-4 py-2 lg:py-4">
        <ResponsiveHighchartsReact
          highcharts={Highcharts}
          options={options}
        />
      </div>
    </div>
  );
};

const AR6ScenarioChart = ({ countryIso }) => {

  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* DATA FETCH */
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        await initializeFuelColors();
        const response = await energyMetricsAPI.getAr6ScenarioDrivers(countryIso);
        
        if (response?.success && response?.data) {
          setChartData(response.data);
        } else {
          throw new Error(
            response?.error ||
            'Failed to load AR6 scenario data'
          );
        }
      } catch (err) {
        console.error(err);
        setError(err.message);
        toast.error(
          `Failed to load AR6 scenario data: ${err.message}`
        );
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [countryIso]);

  /* COMMON FORMATTER */
  const tooltipFormatter = function () {
    const formatted =
      this.y !== null && this.y !== undefined
        ? smartFormatNumber(this.y)
        : 'N/A';

    return `<b>${this.series.name}</b><br/>${this.x}: ${formatted}`;
  };

  /* SERIES BUILDERS */
  const buildLineSeries = (dataKey) => {
    if (!chartData?.[dataKey]) return [];
    const { categories, colors, years } = chartData;
    return categories.map(category => ({
      name: category,
      data: years.map(year => {
        const value =
          chartData[dataKey]?.[category]?.[year];

        return [
          year,
          value !== undefined && !isNaN(value)
            ? value
            : null
        ];
      }),

      color: colors?.[category],
      lineWidth: 3,
      marker: { enabled: true, radius: 4 }
    }));
  };

  const electricitySeries = useMemo(() => {
    if (!chartData) return [];
    const {
      elec_chart_data,
      iea_years,
      iea_values,
      categories,
      colors,
      years
    } = chartData;

    const historical = {
      name: 'IEA Historical',
      type: 'line',
      dashStyle: 'Dash',
      color: '#666',
      data: iea_years?.map((y, i) => [y, iea_values?.[i]]),
      lineWidth: 2,
      marker: { enabled: true, radius: 3 }
    };

    const projections = categories.map(category => ({
      name: category,
      type: 'line',
      color: colors?.[category],
      data: years.map(year => [
        year,
        elec_chart_data?.[category]?.[year] ?? null
      ]),
      marker: { enabled: true, radius: 4 }
    }));

    return [historical, ...projections];
  }, [chartData]);

  const fuelPriceSeries = useMemo(() => {
    if (!chartData?.fuel_price_chart_data) return [];
    const fuelNames = {
      gas: 'Gas',
      oil: 'Oil',
      coal: 'Coal',
      bioenergy: 'Biomass'
    };

    return Object.keys(
      chartData.fuel_price_chart_data
    ).map(key => ({
      name:
        fuelNames[key] ||
        key.charAt(0).toUpperCase() + key.slice(1),
      data: chartData.years.map(year => [
        year,
        chartData.fuel_price_chart_data?.[key]?.[year] ?? null
      ]),
      color: getFuelColor(key),
      lineWidth: 3,
      marker: { enabled: true, radius: 4 }
    }));
  }, [chartData]);

  const buildShareSeries = (key) => {
    if (!chartData?.[key]) return [];
    const labelMap = {
      'Transportation electricity share': 'Transportation',
      'Industry electricity share': 'Industry',
      'Residential and commercial electricity share':
        'Residential and commercial'
    };

    return Object.keys(chartData[key]).map(name => ({
      name:
        labelMap[name] ||
        name.charAt(0).toUpperCase() + name.slice(1),
      data: chartData.years.map(
        y => chartData[key]?.[name]?.[y] ?? 0
      ),
      stack: 'share'
    }));
  };

  /* CHART CONFIGS */
  const co2Options = useMemo(() => {
    if (!chartData) return null;
    return createLineChartConfig({
      title: 'CO₂ Price Trajectories',
      subtitle:
        'Projected carbon prices across AR6 scenarios',
      height: 350,
      xAxis: { categories: chartData.years },
      yAxis: { title: '$/tCO₂' },
      tooltip: { formatter: tooltipFormatter },
      enableSeriesLabels: true,
      series: buildLineSeries('co2_chart_data')
    });
  }, [chartData]);

  const electricityOptions = useMemo(() => {
    if (!chartData) return null;
    return createLineChartConfig({
      title: 'Electricity Demand Projections',
      subtitle:
        'Historical demand + AR6 projections',
      height: 350,
      yAxis: { title: 'TWh' },
      tooltip: { formatter: tooltipFormatter },
      enableSeriesLabels: true,
      series: electricitySeries
    });
  }, [electricitySeries, chartData]);

  const hydrogenOptions = useMemo(() => {
    if (!chartData) return null;
    return createLineChartConfig({
      title: 'Hydrogen Demand Projections',
      subtitle:
        'Projected hydrogen demand under AR6 scenarios',
      height: 350,
      xAxis: { categories: chartData.years },
      yAxis: { title: 'TWh' },
      tooltip: { formatter: tooltipFormatter },
      enableSeriesLabels: true,
      series: buildLineSeries('hydrogen_chart_data')
    });
  }, [chartData]);

  const fuelPriceOptions = useMemo(() => {
    if (!fuelPriceSeries.length) return null;
    return createLineChartConfig({
      title: 'Fuel Price Projections',
      subtitle: 'C7 scenario fuel prices',
      height: 350,
      xAxis: { categories: chartData.years },
      yAxis: { title: 'USD/MWh' },
      tooltip: { formatter: tooltipFormatter },
      enableSeriesLabels: true,
      series: fuelPriceSeries
    });
  }, [fuelPriceSeries, chartData]);

  const c1Options = useMemo(() => {
    const series = buildShareSeries('c1_elec_share_chart_data');

    if (!series.length) return null;
    return createStackedColumnChartConfig({
      title: 'C1 Electricity Share',
      subtitle: 'Ambitious scenario mix',
      height: 350,
      xAxis: { categories: chartData.years },
      yAxis: { title: '%', max: 100 },
      stacking: 'percent',
      showDataLabels: true,
      series
    });
  }, [chartData]);

  const c7Options = useMemo(() => {
    const series = buildShareSeries('c7_elec_share_chart_data');

    if (!series.length) return null;
    return createStackedColumnChartConfig({
      title: 'C7 Electricity Share',
      subtitle: 'Limited action mix',
      height: 350,
      xAxis: { categories: chartData.years },
      yAxis: { title: '%', max: 100 },
      stacking: 'percent',
      showDataLabels: true,
      series
    });
  }, [chartData]);

  /* STATES */
  if (loading)
    return (
      <div className="w-full min-w-0 px-0 lg:px-4 py-2 lg:py-4 flex justify-center items-center h-56 md:h-64">
        <div className="animate-spin h-10 w-10 border-b-2 border-indigo-500 rounded-full" />
      </div>
    );

  if (error)
    return (
      <div className="w-full min-w-0 px-0 lg:px-4 py-2 lg:py-4 text-center text-red-600 text-sm md:text-base">
        {error}
      </div>
    );

  if (!chartData)
    return (
      <div className="w-full min-w-0 px-0 lg:px-4 py-2 lg:py-4 text-center text-gray-500 text-sm md:text-base">
        No data available
      </div>
    );

  /* UI */
  return (
    <div className="w-full min-w-0 space-y-3 md:space-y-4">
      {chartData.r10_region && (
        <div className="bg-purple-50 border rounded-lg px-3 py-2 sm:px-4 text-xs sm:text-sm text-gray-800 min-w-0 break-words">
          AR6 Region:
          <span className="ml-2 font-mono">
            {chartData.r10_region}
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 min-w-0">
        <ChartCard options={co2Options} />
        <ChartCard options={electricityOptions} />
        <ChartCard options={hydrogenOptions} />
        <ChartCard options={fuelPriceOptions} />
        <ChartCard options={c1Options} />
        <ChartCard options={c7Options} />
      </div>

      <DemandFuelPriceDocumentationAccordion />

    </div>
  );
};

export default AR6ScenarioChart;